import React, { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ThemeProps } from '../styles/theme';

export interface PromptSelectOption {
    value: string;
    label: string;
}

export interface PromptSelect {
    label: string;
    options: PromptSelectOption[];
    defaultValue: string;
}

export interface PromptField {
    label: string;
    placeholder?: string;
    maxLength?: number;
    defaultValue?: string;
    singleLine?: boolean;
}

export interface PromptResult {
    text: string;
    extraText: string;
    selectValue?: string;
}

interface PromptOptions {
    field: PromptField;
    extraField?: PromptField;
    confirmLabel?: string;
    select?: PromptSelect;
}

interface ConfirmDialogProps {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: (value?: PromptResult) => void;
    onCancel: () => void;
    prompt?: PromptOptions;
}

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const DialogContainer = styled.div<ThemeProps>`
    background-color: ${props => props.theme.primaryBackground};
    border-radius: 8px;
    padding: 20px;
    width: 300px;
    max-width: 90%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const Title = styled.h3<ThemeProps>`
    color: ${props => props.theme.primaryText};
    margin-top: 0;
    margin-bottom: 10px;
`;

const Message = styled.p<ThemeProps>`
    color: ${props => props.theme.primaryText};
    margin-bottom: 20px;
`;

const PromptLabel = styled.label<ThemeProps>`
    display: block;
    color: ${props => props.theme.primaryText};
    margin-bottom: 6px;
`;

const promptInputStyles = css<ThemeProps>`
    box-sizing: border-box;
    width: 100%;
    margin-bottom: 20px;
    padding: 8px;
    font-size: 16px;
    color: ${props => props.theme.primaryText};
    background-color: ${props => props.theme.primaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 4px;
`;

const PromptInput = styled.textarea<ThemeProps>`
    ${promptInputStyles};
    min-height: 88px;
    resize: vertical;
`;

const PromptLineInput = styled.input<ThemeProps>`
    ${promptInputStyles};
`;

const PromptSelectInput = styled.select<ThemeProps>`
    box-sizing: border-box;
    width: 100%;
    margin-bottom: 20px;
    padding: 8px;
    font-size: 16px;
    color: ${props => props.theme.primaryText};
    background-color: ${props => props.theme.primaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 4px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const Button = styled.button<ThemeProps>`
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-weight: 500;
`;

const CancelButton = styled(Button)`
    background-color: #e0e0e0;
    color: #333;
    
    &:hover {
        background-color: #d0d0d0;
    }
`;

const ConfirmButton = styled(Button)`
    background-color: #f44336;
    color: white;
    
    &:hover {
        background-color: #d32f2f;
    }
`;

interface PromptTextFieldProps {
    id: string;
    field: PromptField;
    value: string;
    onChange: (value: string) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
}

const PromptTextField: FC<PromptTextFieldProps> = ({ id, field, value, onChange, onKeyDown }) => {
    const inputProps = {
        id,
        'data-testid': id,
        maxLength: field.maxLength,
        placeholder: field.placeholder,
        value,
        onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onChange(event.target.value),
        onKeyDown,
    };

    return (
        <>
            <PromptLabel htmlFor={id}>{field.label}</PromptLabel>
            {field.singleLine === true ? <PromptLineInput {...inputProps}/> : <PromptInput {...inputProps}/>}
        </>
    );
};

const ConfirmDialog: FC<ConfirmDialogProps> = ({ isOpen, title, message, onConfirm, onCancel, prompt }) => {
    const [promptValue, setPromptValue] = useState('');
    const [extraValue, setExtraValue] = useState('');
    const [selectValue, setSelectValue] = useState('');

    useEffect(() => {
        if (isOpen) {
            setPromptValue(prompt?.field.defaultValue ?? '');
            setExtraValue(prompt?.extraField?.defaultValue ?? '');
            setSelectValue(prompt?.select?.defaultValue ?? '');
        }
    }, [isOpen, prompt]);

    if (!isOpen) return null;

    const confirm = () => {
        onConfirm(prompt
            ? {text: promptValue, extraText: extraValue, selectValue: prompt.select ? selectValue : undefined}
            : undefined);
    };

    const keyDownHandler = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
        }
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey || event.currentTarget.tagName === 'INPUT')) {
            event.preventDefault();
            confirm();
        }
    };

    return (
        <Overlay onClick={onCancel}>
            <DialogContainer onClick={e => e.stopPropagation()}>
                <Title>{title}</Title>
                <Message>{message}</Message>
                {prompt && (
                    <>
                        <PromptTextField
                            id="confirm-dialog-prompt"
                            field={prompt.field}
                            value={promptValue}
                            onChange={setPromptValue}
                            onKeyDown={keyDownHandler}
                        />
                        {prompt.extraField && (
                            <PromptTextField
                                id="confirm-dialog-prompt-extra"
                                field={prompt.extraField}
                                value={extraValue}
                                onChange={setExtraValue}
                                onKeyDown={keyDownHandler}
                            />
                        )}
                        {prompt.select && (
                            <>
                                <PromptLabel htmlFor="confirm-dialog-select">{prompt.select.label}</PromptLabel>
                                <PromptSelectInput
                                    id="confirm-dialog-select"
                                    data-testid="confirm-dialog-select"
                                    value={selectValue}
                                    onChange={event => setSelectValue(event.target.value)}
                                >
                                    {prompt.select.options.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </PromptSelectInput>
                            </>
                        )}
                    </>
                )}
                <ButtonContainer>
                    <CancelButton onClick={onCancel}>Cancel</CancelButton>
                    <ConfirmButton
                        data-testid="confirm-dialog-confirm"
                        onClick={confirm}
                    >
                        {prompt?.confirmLabel ?? 'Confirm'}
                    </ConfirmButton>
                </ButtonContainer>
            </DialogContainer>
        </Overlay>
    );
};

export default ConfirmDialog;
